/* ==========================================================================
   GPUElite R4 — global.js
   Deferred-safe, DOMContentLoaded-safe vanilla JS
   ========================================================================== */
(function () {
  'use strict';

  /* -----------------------------------------------------------------------
     UTILITIES
     ----------------------------------------------------------------------- */

  /**
   * Fire a callback when DOM is ready.
   * @param {Function} fn
   */
  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  }

  /**
   * Delegated event listener on document.
   * @param {string} event
   * @param {string} selector
   * @param {Function} handler
   */
  function on(event, selector, handler) {
    document.addEventListener(event, function (e) {
      var target = e.target.closest(selector);
      if (target) handler.call(target, e, target);
    });
  }

  /* -----------------------------------------------------------------------
     ANNOUNCEMENT BAR — dismiss
     ----------------------------------------------------------------------- */
  function initAnnouncementBar() {
    var bar = document.querySelector('.r4-announce');
    if (!bar) return;

    var btn = bar.querySelector('.r4-announce__dismiss');
    var key = 'r4_announce_dismissed';

    // Restore dismissed state
    try {
      if (window.localStorage.getItem(key) === '1') {
        bar.style.display = 'none';
        return;
      }
    } catch (err) {
      // localStorage may be unavailable in restricted contexts.
    }

    if (!btn) return;

    btn.addEventListener('click', function () {
      bar.style.display = 'none';
      try {
        window.localStorage.setItem(key, '1');
      } catch (err) {
        // localStorage not available — dismiss for session only
      }
    });
  }

  /* -----------------------------------------------------------------------
     MOBILE NAV — toggle
     ----------------------------------------------------------------------- */
  function initMobileNav() {
    var toggle = document.querySelector('.r4-menu-toggle');
    var nav = document.querySelector('.r4-nav');
    if (!toggle || !nav) return;

    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('aria-controls', 'r4-main-nav');
    nav.id = 'r4-main-nav';

    toggle.addEventListener('click', function () {
      var isOpen = nav.classList.toggle('r4-nav--open');
      toggle.setAttribute('aria-expanded', String(isOpen));
      toggle.textContent = isOpen ? '\u2715' : '\u2630';
      document.body.style.overflow = isOpen ? 'hidden' : '';
    });

    // Close on nav link click
    on('click', '.r4-nav--open a', function () {
      nav.classList.remove('r4-nav--open');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.textContent = '\u2630';
      document.body.style.overflow = '';
    });

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && nav.classList.contains('r4-nav--open')) {
        nav.classList.remove('r4-nav--open');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.textContent = '\u2630';
        document.body.style.overflow = '';
        toggle.focus();
      }
    });
  }

  /* -----------------------------------------------------------------------
     PRODUCT PAGE — variant picker
     ----------------------------------------------------------------------- */
  function initVariantPicker() {
    var form = document.querySelector('.r4-product-form');
    if (!form) return;

    var productJson = document.querySelector('[data-product-json]');
    if (!productJson) return;

    var product;
    try {
      product = JSON.parse(productJson.textContent || productJson.innerText);
    } catch (e) {
      return;
    }

    var atcBtn = form.querySelector('[data-atc-btn]');
    var buyNowBtn = form.querySelector('[data-buy-now-btn]');
    var qtyInput = form.querySelector('[name="quantity"]');
    var currentVariantIdInput = form.querySelector('[name="id"]');
    var root = form.closest('.r4-buy-panel') || document;
    var priceEls = document.querySelectorAll('[data-price], .r4-mobile-atc__price');
    var comparePriceEl = root.querySelector('[data-compare-price]');
    var stockStatusEl = root.querySelector('[data-stock-status]');

    var selectedOptions = {};

    function showAddToCartToast() {
      var existing = document.querySelector('.r9-atc-toast');
      if (existing) existing.remove();
      var toast = document.createElement('div');
      toast.className = 'r9-atc-toast';
      toast.setAttribute('role', 'status');
      toast.setAttribute('aria-live', 'polite');
      toast.innerHTML = '<strong>Added to cart</strong><p>Review your cart or continue straight to secure checkout.</p><div class="r9-atc-toast__actions"><a class="r4-btn r4-btn--primary r4-btn--sm" href="/checkout">Checkout</a><a class="r4-btn r4-btn--secondary r4-btn--sm" href="/cart">View cart</a></div>';
      document.body.appendChild(toast);
      setTimeout(function () {
        if (toast && toast.parentNode) toast.parentNode.removeChild(toast);
      }, 6500);
    }

    // Initialize selected options from radio inputs
    form.querySelectorAll('.r4-variant-group input[type="radio"]').forEach(function (radio) {
      var match = radio.name.match(/^options\[(.+)\]$/);
      if (match) {
        if (radio.checked) {
          selectedOptions[match[1]] = radio.value;
        }
        // Set up label click
        var label = document.querySelector('label[for="' + radio.id + '"]');
        if (label) {
          radio.addEventListener('change', function () {
            updateSelectedLabels(form);
            var variant = resolveVariant(product, selectedOptions);
            updateUI(variant);
          });
        }
      }
    });

    // Qty input
    if (qtyInput) {
      qtyInput.addEventListener('change', function () {
        var val = parseInt(qtyInput.value, 10);
        if (isNaN(val) || val < 1) qtyInput.value = 1;
      });
    }

    /**
     * Resolve a variant from product data given current selected options.
     */
    function resolveVariant(prod, selected) {
      return prod.variants.find(function (v) {
        return prod.options.every(function (optName, index) {
          return v.options[index] === selected[optName];
        });
      }) || null;
    }

    /**
     * Update visual label states.
     */
    function updateSelectedLabels(frm) {
      frm.querySelectorAll('.r4-variant-group').forEach(function (group) {
        var inputs = group.querySelectorAll('input[type="radio"]');
        inputs.forEach(function (input) {
          var label = document.querySelector('label[for="' + input.id + '"]');
          if (!label) return;
          label.classList.remove('r4-variant-label--selected');
          if (input.checked) {
            label.classList.add('r4-variant-label--selected');
            var match = input.name.match(/^options\[(.+)\]$/);
            if (match) selectedOptions[match[1]] = input.value;
          }
        });
      });
    }

    /**
     * Update UI for a resolved variant.
     */
    function updateUI(variant) {
      if (!variant) {
        // No matching variant — disable buttons
        if (atcBtn) {
          atcBtn.disabled = true;
          atcBtn.textContent = 'Unavailable';
        }
        if (buyNowBtn) {
          buyNowBtn.disabled = true;
        }
        return;
      }

      // Update hidden variant ID
      if (currentVariantIdInput) {
        currentVariantIdInput.value = variant.id;
      }

      // Update URL
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      // Update price
      priceEls.forEach(function (priceEl) {
        priceEl.textContent = formatMoney(variant.price);
      });
      if (comparePriceEl) {
        comparePriceEl.style.display = 'none';
        comparePriceEl.innerHTML = '';
      }

      // Update availability status without exposing inventory-depth hints
      if (stockStatusEl) {
        stockStatusEl.innerHTML = variant.available
          ? ''
          : '<span class="r4-badge r4-badge--red">Unavailable</span>';
      }

      // Update buttons
      if (variant.available) {
        if (atcBtn) {
          atcBtn.disabled = false;
          atcBtn.removeAttribute('aria-disabled');
          atcBtn.textContent = atcBtn.dataset.originalText || 'Add to Cart';
        }
        if (buyNowBtn) {
          buyNowBtn.disabled = false;
          buyNowBtn.removeAttribute('aria-disabled');
          buyNowBtn.textContent = buyNowBtn.dataset.originalText || 'Buy It Now';
        }
        document.querySelectorAll('[data-mobile-atc]').forEach(function (btn) {
          btn.disabled = false;
          btn.removeAttribute('aria-disabled');
          btn.textContent = 'Add to Cart';
        });
      } else {
        if (atcBtn) {
          atcBtn.disabled = true;
          atcBtn.setAttribute('aria-disabled', 'true');
          atcBtn.textContent = 'Sold Out';
        }
        if (buyNowBtn) {
          buyNowBtn.disabled = true;
          buyNowBtn.setAttribute('aria-disabled', 'true');
          buyNowBtn.textContent = 'Unavailable';
        }
        document.querySelectorAll('[data-mobile-atc]').forEach(function (btn) {
          btn.disabled = true;
          btn.setAttribute('aria-disabled', 'true');
          btn.textContent = 'Sold Out';
        });
      }
    }

    // Add to Cart handler
    if (atcBtn) {
      atcBtn.dataset.originalText = atcBtn.textContent;
      atcBtn.addEventListener('click', function (e) {
        e.preventDefault();
        var variantId = currentVariantIdInput ? currentVariantIdInput.value : '';
        var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

        if (!variantId) return;

        atcBtn.disabled = true;
        atcBtn.textContent = 'Adding\u2026';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ id: Number(variantId), quantity: qty })
        })
          .then(function (res) {
            if (!res.ok) throw new Error('Unable to add item');
            return res.json();
          })
          .then(function () {
            atcBtn.textContent = 'Added!';
            updateCartCount();
            showAddToCartToast();
            setTimeout(function () {
              atcBtn.textContent = atcBtn.dataset.originalText || 'Add to Cart';
              atcBtn.disabled = false;
            }, 2000);
          })
          .catch(function () {
            atcBtn.textContent = 'Error — Retry';
            atcBtn.disabled = false;
          });
      });
    }

    // Buy It Now handler
    if (buyNowBtn) {
      buyNowBtn.dataset.originalText = buyNowBtn.textContent;
      var buyNowError = document.querySelector('[data-buy-now-error]');
      buyNowBtn.addEventListener('click', function () {
        var variantId = currentVariantIdInput ? currentVariantIdInput.value : '';
        var qty = qtyInput ? parseInt(qtyInput.value, 10) || 1 : 1;

        if (!variantId) {
          if (buyNowError) {
            buyNowError.hidden = false;
            buyNowError.textContent = 'Choose an available option before checkout.';
          }
          return;
        }

        if (buyNowError) {
          buyNowError.hidden = true;
          buyNowError.textContent = '';
        }
        buyNowBtn.disabled = true;
        buyNowBtn.textContent = 'Preparing\u2026';

        fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ id: Number(variantId), quantity: qty })
        })
          .then(function (res) {
            if (!res.ok) throw new Error('Unable to add item');
            window.location.href = '/checkout';
          })
          .catch(function () {
            buyNowBtn.disabled = false;
            buyNowBtn.textContent = 'Go to Cart';
            if (buyNowError) {
              buyNowError.hidden = false;
              buyNowError.textContent = 'Immediate checkout could not start. The item may still be added from the cart.';
            }
            setTimeout(function () { window.location.href = '/cart'; }, 900);
          });
      });
    }

    // Handle URL variant param on load
    var urlVariant = new URL(window.location.href).searchParams.get('variant');
    if (urlVariant && product.variants) {
      var preSelect = product.variants.find(function (v) { return String(v.id) === String(urlVariant); });
      if (preSelect) {
        preSelect.options.forEach(function (val, i) {
          var optName = product.options[i] || null;
          if (!optName) return;
          selectedOptions[optName] = val;
          var radio = form.querySelector(
            'input[type="radio"][name="options[' + optName + ']"][value="' + CSS.escape(val) + '"]'
          );
          if (radio) {
            radio.checked = true;
          }
        });
        updateSelectedLabels(form);
        updateUI(preSelect);
      }
    } else {
      // Default to first available variant
      var firstAvailable = product.variants.find(function (v) { return v.available; });
      if (firstAvailable) {
        firstAvailable.options.forEach(function (val, i) {
          var optName = product.options[i] || null;
          if (!optName) return;
          selectedOptions[optName] = val;
        });
        updateSelectedLabels(form);
        updateUI(firstAvailable);
      } else if (product.variants && product.variants.length) {
        updateUI(product.variants[0]);
      }
    }
  }

  /* -----------------------------------------------------------------------
     GALLERY — thumbnail click to swap main image
     ----------------------------------------------------------------------- */
  function initGallery() {
    var mainImg = document.querySelector('.r4-gallery__main img');
    var thumbs = document.querySelectorAll('.r4-gallery__thumb');
    if (!mainImg || !thumbs.length) return;

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var newSrc = thumb.dataset.fullSrc || thumb.querySelector('img').src;
        var newAlt = thumb.querySelector('img').alt || '';

        // Update main image
        mainImg.src = newSrc;
        mainImg.alt = newAlt;

        // Update active state
        thumbs.forEach(function (t) { t.classList.remove('r4-gallery__thumb--active'); });
        thumb.classList.add('r4-gallery__thumb--active');
      });
    });

    // Set first thumb active
    if (thumbs[0]) thumbs[0].classList.add('r4-gallery__thumb--active');
  }

  /* -----------------------------------------------------------------------
     PRODUCT TABS — ARIA tabs
     ----------------------------------------------------------------------- */
  function initTabs() {
    var tabLists = document.querySelectorAll('[role="tablist"]');
    tabLists.forEach(function (tablist) {
      var tabs = tablist.querySelectorAll('[role="tab"]');

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var panelId = tab.getAttribute('aria-controls');
          var panel = document.getElementById(panelId);
          if (!panel) return;

          tabs.forEach(function (t) {
            t.setAttribute('aria-selected', 'false');
            t.setAttribute('tabindex', '-1');
          });
          tab.setAttribute('aria-selected', 'true');
          tab.setAttribute('tabindex', '0');

          document.querySelectorAll('[role="tabpanel"]').forEach(function (p) {
            p.hidden = true;
          });
          panel.hidden = false;
        });

        // Arrow key navigation
        tab.addEventListener('keydown', function (e) {
          var currentIndex = Array.from(tabs).indexOf(tab);
          var nextIndex;

          if (e.key === 'ArrowRight') {
            nextIndex = (currentIndex + 1) % tabs.length;
          } else if (e.key === 'ArrowLeft') {
            nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          } else if (e.key === 'Home') {
            nextIndex = 0;
          } else if (e.key === 'End') {
            nextIndex = tabs.length - 1;
          } else {
            return;
          }

          e.preventDefault();
          tabs[nextIndex].focus();
          tabs[nextIndex].click();
        });
      });
    });
  }

  /* --------------------------------------------------------------------===
     MOBILE STICKY ATC BAR — IntersectionObserver
     ----------------------------------------------------------------------- */
  function initMobileATC() {
    var bar = document.querySelector('.r4-mobile-atc');
    var buyPanel = document.querySelector('.r4-buy-panel');
    if (!bar || !buyPanel) return;

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          bar.style.display = entry.isIntersecting ? 'none' : 'flex';
        });
      },
      { threshold: 0, rootMargin: '-72px 0px 0px 0px' }
    );

    observer.observe(buyPanel);

    var mobileBtn = bar.querySelector('[data-mobile-atc]');
    var mainAtcBtn = document.querySelector('[data-atc-btn]');
    if (mobileBtn && mainAtcBtn) {
      mobileBtn.addEventListener('click', function () {
        mainAtcBtn.click();
      });
    }
  }

  /* -----------------------------------------------------------------------
     FILTER MOBILE DRAWER — dialog semantics
     ----------------------------------------------------------------------- */
  function initFilterDrawer() {
    var openBtn = document.querySelector('[data-filter-toggle]');
    var closeBtn = document.querySelector('[data-filter-close]');
    var drawer = document.querySelector('[data-filter-drawer]');
    if (!openBtn || !drawer) return;

    function openDrawer() {
      drawer.hidden = false;
      drawer.setAttribute('aria-modal', 'true');
      document.body.style.overflow = 'hidden';
      // Focus first focusable in drawer
      var focusable = drawer.querySelector('button, input, select, a[href]');
      if (focusable) focusable.focus();
    }

    function closeDrawer() {
      drawer.hidden = true;
      drawer.removeAttribute('aria-modal');
      document.body.style.overflow = '';
      openBtn.focus();
    }

    openBtn.addEventListener('click', openDrawer);
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

    // Close on Escape
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !drawer.hidden) {
        closeDrawer();
      }
    });

    // Close on backdrop click
    drawer.addEventListener('click', function (e) {
      if (e.target === drawer) closeDrawer();
    });
  }

  /* -----------------------------------------------------------------------
     COLLECTION FILTERS — explicit Apply button + compact single max-price slider
     ----------------------------------------------------------------------- */
  function initCollectionFilterOptions() {
    // Filters are intentionally explicit-apply. Do not auto-submit on checkbox/radio change.
    document.querySelectorAll('[data-filter-pending-note]').forEach(function (note) {
      note.hidden = false;
    });
  }

  function initPriceRangeFilters() {
    document.querySelectorAll('[data-price-filter]').forEach(function (wrap) {
      var maxRange = wrap.querySelector('[data-price-max-range]');
      var minInput = wrap.querySelector('[data-price-min-input]');
      var maxInput = wrap.querySelector('[data-price-max-input]');
      var maxOut = wrap.querySelector('[data-price-max-output]');
      if (!maxRange || !maxInput) return;

      function normalize() {
        var maxLimit = parseFloat(maxRange.max || 0);
        var max = parseFloat(maxRange.value || maxLimit || 0);
        if (max < 0) max = 0;
        if (maxLimit && max > maxLimit) max = maxLimit;

        maxRange.value = String(Math.round(max));
        maxInput.value = maxLimit && max < maxLimit ? String(Math.round(max)) : '';
        if (minInput) minInput.value = '';
        if (maxOut) maxOut.textContent = String(Math.round(max));
      }

      maxRange.addEventListener('input', normalize);
      maxRange.addEventListener('change', normalize);
      maxInput.addEventListener('input', function () {
        maxRange.value = maxInput.value || maxRange.max;
        normalize();
      });
      maxInput.addEventListener('change', normalize);
      maxInput.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          normalize();
        }
      });
      normalize();
    });
  }

  /* -----------------------------------------------------------------------
     CART — AJAX quantity update
     ----------------------------------------------------------------------- */
  function initCartQty() {
    on('change', '[data-cart-qty]', function (e) {
      var input = e.target;
      var line = input.dataset.cartLine;
      var qty = parseInt(input.value, 10);

      if (isNaN(qty) || qty < 0) {
        input.value = 0;
        return;
      }

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: qty })
      })
        .then(function (res) { return res.ok ? res.json() : Promise.reject(); })
        .then(function () { window.location.reload(); })
        .catch(function () {
          // Reload on error to show correct state
          window.location.reload();
        });
    });
  }

  /* -----------------------------------------------------------------------
     CART — AJAX remove item
     ----------------------------------------------------------------------- */
  function initCartRemove() {
    on('click', '[data-cart-remove]', function (e, btn) {
      var line = btn && btn.dataset ? btn.dataset.cartLine : '';

      // If line data is missing, do not block Shopify's native remove URL fallback.
      if (!line || isNaN(Number(line))) {
        return;
      }

      e.preventDefault();
      btn.setAttribute('aria-disabled', 'true');
      btn.textContent = 'Removing…';

      fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: 0 })
      })
        .then(function (res) { return res.ok ? res.json() : Promise.reject(); })
        .then(function () { window.location.reload(); })
        .catch(function () {
          // Preserve the server-rendered Shopify fallback if AJAX fails.
          window.location.href = btn.href;
        });
    });
  }

  /* -----------------------------------------------------------------------
     CART COUNT — update header cart indicator
     ----------------------------------------------------------------------- */
  function updateCartCount() {
    fetch('/cart.js', {
      method: 'GET',
      headers: { 'Accept': 'application/json' }
    })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var indicators = document.querySelectorAll('.r4-cart-link__count');
        indicators.forEach(function (el) {
          el.textContent = cart.item_count;
        });
        var dot = document.querySelector('.r4-cart-link__dot');
        if (dot) {
          dot.classList.toggle('r4-cart-link__dot--empty', cart.item_count === 0);
        }
      })
      .catch(function () {
        // Silently fail — cart count not critical
      });
  }

  /* -----------------------------------------------------------------------
     VOLUME METER — animate fill on page load
     ----------------------------------------------------------------------- */
  function initVolumeMeter() {
    var meter = document.querySelector('[data-volume-meter]');
    if (!meter) return;

    var fill = meter.querySelector('.r4-volume__bar-fill');
    if (!fill) return;

    // Read target width from data attribute
    var targetWidth = meter.dataset.volumeMeter;
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      fill.style.width = targetWidth;
      return;
    }

    // Animate after short delay (not blocking)
    setTimeout(function () {
      fill.style.width = targetWidth;
    }, 100);
  }

  /* -----------------------------------------------------------------------
     OPTIONAL VIDEO — deferred load on toggle
     ----------------------------------------------------------------------- */
  function initHeroVideo() {
    var toggle = document.querySelector('.r4-hero__video-toggle');
    var container = document.querySelector('.r4-hero__video-container');
    if (!toggle || !container) return;

    toggle.addEventListener('click', function () {
      var video = container.querySelector('video');
      if (!video) {
        // Create video element dynamically
        var src = toggle.dataset.src;
        if (!src) return;

        video = document.createElement('video');
        video.src = src;
        video.autoplay = true;
        video.muted = true;
        video.loop = true;
        video.playsInline = true;
        video.preload = 'none';

        var mediaWrap = container.closest('.r4-hero__image-wrap');
        var img = mediaWrap ? mediaWrap.querySelector('img') : null;
        if (img) img.style.display = 'none';
        container.hidden = false;

        container.appendChild(video);
        toggle.textContent = 'Pause';
        toggle.setAttribute('aria-pressed', 'true');
      } else {
        if (video.paused) {
          video.play();
          toggle.textContent = 'Pause';
          toggle.setAttribute('aria-pressed', 'true');
        } else {
          video.pause();
          toggle.textContent = 'Play';
          toggle.setAttribute('aria-pressed', 'false');
        }
      }
    });
  }

  /* -----------------------------------------------------------------------
     CONTENT ACCESS
     Copy protection intentionally disabled: hardware buyers need to copy model
     names, SKUs, and specs for comparison.
     ----------------------------------------------------------------------- */

  /* -----------------------------------------------------------------------
     DESCRIPTION EXPAND — "Read more" for long product descriptions
     ----------------------------------------------------------------------- */
  function initDescriptionExpand() {
    var expandBtn = document.querySelector('.r4-description-expand');
    if (!expandBtn) return;
    expandBtn.addEventListener('click', function () {
      var full = document.getElementById(expandBtn.getAttribute('aria-controls'));
      if (!full) return;
      var expanded = expandBtn.getAttribute('aria-expanded') === 'true';
      expandBtn.setAttribute('aria-expanded', String(!expanded));
      expandBtn.textContent = expanded ? 'Read more' : 'Show less';
      if (expanded) {
        full.setAttribute('hidden', '');
      } else {
        full.removeAttribute('hidden');
      }
    });
  }

  /* -----------------------------------------------------------------------
     FORMAT MONEY — Shopify money filter equivalent
     ----------------------------------------------------------------------- */
  function formatMoney(cents) {
    var dollars = cents / 100;
    return '$' + dollars.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  /* -----------------------------------------------------------------------
     INIT ALL
     ----------------------------------------------------------------------- */
  onReady(function () {
    if (window.location.pathname.indexOf('/policies/') === 0) {
      document.body.classList.add('template-policy');
    }
    initAnnouncementBar();
    initMobileNav();
    initVariantPicker();
    initGallery();
    initTabs();
    initMobileATC();
    initFilterDrawer();
    initCollectionFilterOptions();
    initPriceRangeFilters();
    initCartQty();
    initCartRemove();
    initVolumeMeter();
    initHeroVideo();
    initDescriptionExpand();
  });

})();
